import isEnrollmentEffective from '@/utils/members/isEnrollmentEffective';
import { logPerformanceMetric } from '@/utils/metrics';

/**
 * Determines if a member has effective coverage (at a given effective date) given their insurance enrollments.
 *
 * This function evaluates a member's coverage effectiveness by checking if they have effective coverage
 * within active enrollment periods. It uses intersection logic to determine if the
 * effective date falls within both enrollment and coverage periods.
 *
 * @param {Object} member - The member object containing enrollment data
 * @param {Object[]} member.enrollments - Array of enrollment objects
 * @param {string|Date} member.enrollments[].eligibilityStartDate - Enrollment effective start date
 * @param {string|Date} member.enrollments[].eligibilityEndDate - Enrollment effective end date
 * @param {Object[]} member.enrollments[].coverages - Array of coverage objects within enrollment
 * @param {string|Date} member.enrollments[].coverages[].startDate - Coverage effective start date (alternative: coverageStartDate)
 * @param {string|Date} member.enrollments[].coverages[].endDate - Coverage effective end date (alternative: coverageEndDate)
 * @param {string|Date} member.enrollments[].coverages[].coverageStartDate - Alternative coverage effective start date field
 * @param {string|Date} member.enrollments[].coverages[].coverageEndDate - Alternative coverage effective end date field
 * @param {Date} [effectiveDate=new Date()] - The date to check effective coverage against (defaults to current date)
 * @returns {boolean} `true` if the member has effective coverage at the effective date, `false` otherwise
 *
 * @example
 * const member = {
 *   enrollments: [{
 *     eligibilityStartDate: '2024-01-01',
 *     eligibilityEndDate: '2024-12-31',
 *     coverages: [{
 *       coverageStartDate: '2024-01-01',
 *       coverageEndDate: '2024-12-31'
 *     }]
 *   }]
 * };
 *
 * const hasEffectiveCoverage = doesMemberHaveEffectiveInsuranceCoverage(member); // Check against current date
 * const hadEffectiveCoverage = doesMemberHaveEffectiveInsuranceCoverage(member, new Date('2024-06-15')); // Check against specific date
 */
const doesMemberHaveEffectiveInsuranceCoverage = (
  member,
  effectiveDate = new Date(),
) =>
  logPerformanceMetric(
    'doesMemberHaveEffectiveInsuranceCoverage',
    () => {
      try {
        // Validate input parameters
        if (!member) {
          // Member object is null or undefined
          return false;
        }

        // Create a local variable to avoid parameter reassignment
        let validatedEffectiveDate = effectiveDate;
        if (
          !validatedEffectiveDate ||
          !(validatedEffectiveDate instanceof Date) ||
          Number.isNaN(validatedEffectiveDate.getTime())
        ) {
          // Invalid effective date provided; use current date as fallback
          validatedEffectiveDate = new Date();
        }

        // Return false if member has no enrollments
        if (!member?.enrollments?.length) {
          // This is expected behavior, not an error
          return false;
        }

        // Validate enrollments array structure
        if (!Array.isArray(member.enrollments)) {
          // Enrollments is not an array
          return false;
        }

        // Check each enrollment to see if any provide effective coverage
        return member.enrollments.some((enrollment) =>
          isEnrollmentEffective(enrollment, validatedEffectiveDate),
        );
      } catch (error) {
        // Unexpected error during effective coverage calculation
        // Graceful degradation: return false (inactive) when calculation fails
        return false;
      }
    },
    {
      memberUuid: member?.uuid,
      effectiveDate: effectiveDate?.toISOString(),
    },
  );

export default doesMemberHaveEffectiveInsuranceCoverage;
